import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface WizardStepperProps {
  currentStep: number; // 1-6
  onStepClick: (step: number) => void;
}

const STEP_NUMBERS = [1, 2, 3, 4, 5, 6] as const;

export function WizardStepper({
  currentStep,
  onStepClick,
}: WizardStepperProps) {
  const { t } = useTranslation("search");

  // Module-level const array moved inside component so entries can call t()
  const STEPS = STEP_NUMBERS.map((n) => ({
    number: n,
    label: t(`wizard.stepper.steps.${n}`),
  }));

  return (
    <nav aria-label={t("wizard.stepper.ariaLabel")} className="w-full">
      <ol className="flex items-center w-full">
        {STEPS.map((step, index) => {
          const isCompleted = step.number < currentStep;
          const isActive = step.number === currentStep;
          const isFuture = step.number > currentStep;
          const isClickable = isCompleted || isActive;
          const isLastStep = index === STEPS.length - 1;

          const ariaLabel = isCompleted
            ? t("wizard.stepper.stepAriaLabel_completed", {
                number: step.number,
                label: step.label,
              })
            : isActive
              ? t("wizard.stepper.stepAriaLabel_current", {
                  number: step.number,
                  label: step.label,
                })
              : t("wizard.stepper.stepAriaLabel_future", {
                  number: step.number,
                  label: step.label,
                });

          return (
            <li
              key={step.number}
              className="flex items-center flex-1 last:flex-none"
            >
              {/* Step item */}
              <button
                type="button"
                disabled={isFuture}
                onClick={() => isClickable && onStepClick(step.number)}
                aria-current={isActive ? "step" : undefined}
                aria-label={ariaLabel}
                className={cn(
                  "flex flex-col items-center gap-1.5 select-none",
                  isClickable ? "cursor-pointer" : "cursor-default",
                )}
              >
                {/* Circle */}
                <span
                  className={cn(
                    "flex h-[26px] w-[26px] items-center justify-center rounded-full text-xs font-semibold transition-colors",
                    isCompleted && "bg-success text-white",
                    isActive && "bg-primary text-primary-foreground",
                    isFuture && "bg-muted text-muted-foreground",
                  )}
                >
                  {isCompleted ? (
                    <Check
                      className="h-3.5 w-3.5 stroke-[2.5]"
                      aria-hidden="true"
                    />
                  ) : (
                    <span>{step.number}</span>
                  )}
                </span>

                {/* Label */}
                <span
                  className={cn(
                    "hidden sm:inline text-[0.6875rem] font-medium transition-colors whitespace-nowrap",
                    isCompleted && "text-success",
                    isActive && "text-primary",
                    isFuture && "text-muted-foreground",
                  )}
                >
                  {step.label}
                </span>
              </button>

              {/* Connector line */}
              {!isLastStep && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "h-px flex-1 mx-1.5 transition-colors",
                    step.number < currentStep ? "bg-success" : "bg-border",
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
